/**
 * Baseline Accounting's formulas, read off the workbook itself.
 *
 * IAS accounting's formulas panel is TRANSCRIBED from an engine, because its numbers come
 * from Python. Baseline Accounting's come from cells, so its panel is not written at all:
 * every line is the formula the model evaluates, as the cell holds it, with the documented
 * fix that replaced it where one did. That is the difference between the two disciplines
 * made visible - one explains rules, the other shows a spreadsheet's own formulas.
 *
 * For each row: the formula of the first historical year and of the first forecast year
 * (the rest of a row is the same formula one column on), the sheets it reads, and the fix.
 */

import type { FormulaGroup } from '../FormulasPanelV2';
import type { FormulaEntry, FormulaSection } from '../formulaTypes';
import type { MCell, MComputed, MFix, MRow, MTab } from '../../../store/m-accounting';

/** A sheet as a reader knows it: "(AI) Balance Sheet" is the Balance Sheet. */
export function sheetLabel(sheet: string): string {
  return sheet.replace(/^\(AI\)\s*/, '').trim();
}

/** The other sheets a formula reads - quoted ('P&L '!A1) or bare (COGS!A1). */
export function sheetsRead(formula: string, own: string): string[] {
  const found = new Set<string>();
  for (const match of formula.matchAll(/'([^']+)'!|([A-Za-z][A-Za-z0-9_]*)!/g)) {
    const name = (match[1] ?? match[2]).trim();
    if (name !== own.trim()) found.add(sheetLabel(name));
  }
  return [...found];
}

function firstFormula(row: MRow, tab: MTab, years: Set<number>): { year: number; col: string; cell: MCell } | null {
  for (const { col, year } of tab.columns) {
    const cell = row.cells[col];
    if (year !== null && years.has(year) && cell?.formula) return { year, col, cell };
  }
  return null;
}

function entryFor(row: MRow, tab: MTab, computed: MComputed, fixes: Map<string, MFix>): FormulaEntry | null {
  const historical = firstFormula(row, tab, new Set(computed.historical));
  const forecast = firstFormula(row, tab, new Set(computed.forecast));
  if (!historical && !forecast) return null;

  const lines = [historical, forecast]
    .filter((x): x is NonNullable<typeof x> => x !== null)
    .map(({ year, col, cell }) => `FY${year} (${col}${row.row})  ${cell.formula}`);
  // One line when both years hold the same pattern, shown for the first.
  const shown = historical && forecast && historical.cell.formula === forecast.cell.formula ? [lines[0]] : lines;

  const fixId = Object.values(row.cells).find((c) => c.fix)?.fix ?? null;
  const fix = fixId ? fixes.get(fixId) : undefined;

  const typedIn = tab.columns.some(({ col, year }) => {
    const cell = row.cells[col];
    return year !== null && computed.historical.includes(year) && cell?.input && !cell.formula;
  });
  const isDefault = Object.values(row.cells).some((c) => c.input && c.formula);
  const notes = [
    typedIn && forecast ? 'Typed in for the historical years; calculated for the forecast.' : null,
    isDefault ? 'A default: enter a figure in the cell to replace the formula.' : null,
  ].filter(Boolean);

  const reads = new Set<string>();
  for (const cell of Object.values(row.cells)) {
    if (cell.formula) sheetsRead(cell.formula, tab.sheet).forEach((s) => reads.add(s));
  }

  return {
    label: row.label || `Row ${row.row}`,
    formula: shown.join('\n'),
    sourceLabels: [...reads],
    note: notes.length ? notes.join(' ') : undefined,
    fix: fixId ? { id: fixId, title: fix?.title ?? '' } : undefined,
  };
}

/** A tab's rows, grouped under the section headings the sheet itself uses. */
function sectionsFor(tab: MTab, computed: MComputed, fixes: Map<string, MFix>): FormulaSection[] {
  const sections: FormulaSection[] = [];
  let current: FormulaSection = { section: sheetLabel(tab.name), entries: [] };
  for (const row of tab.rows) {
    const isHeading = Object.keys(row.cells).length === 0 && row.label && row.label === row.label.toUpperCase();
    if (isHeading) {
      if (current.entries.length) sections.push(current);
      current = { section: row.label.replace(/:$/, ''), entries: [] };
      continue;
    }
    const entry = entryFor(row, tab, computed, fixes);
    if (entry) current.entries.push(entry);
  }
  if (current.entries.length) sections.push(current);
  return sections;
}

export function baselineFormulaGroups(computed: MComputed, fixes: MFix[] | null): FormulaGroup[] {
  const byId = new Map((fixes ?? []).map((f) => [f.id, f]));
  const groups: FormulaGroup[] = computed.tabs
    .map((tab) => ({
      key: tab.sheet,
      label: sheetLabel(tab.name),
      sections: sectionsFor(tab, computed, byId),
    }))
    .filter((group) => group.sections.length > 0);

  if (fixes && fixes.length) {
    groups.push({
      key: 'fixes',
      label: 'Fixes',
      sections: [
        {
          section: 'Every correction to the source workbook, in the order it applies',
          entries: fixes.map((f) => {
            const cells = f.changes.map((c) => `${sheetLabel(c.sheet)}!${c.cell}`);
            return {
              label: `${f.id} - ${f.title}`,
              formula: f.why,
              note:
                `${cells.length} ${cells.length === 1 ? 'cell' : 'cells'}: ${cells.slice(0, 8).join(', ')}` +
                (cells.length > 8 ? `, and ${cells.length - 8} more` : '') +
                (f.default ? '. A default - an entered figure replaces it.' : '.'),
            };
          }),
        },
      ],
    });
  }
  return groups;
}
